import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import {
  getPaymentStatusDisplay,
  getPaymentTypeDisplay,
  PaymentFilters
} from '../../../../../../models/payment';
import { propExists, searchList } from '../../../../../../utils';
import { Association } from '../../../../../../models/association';
import { Observable, OperatorFunction } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { CapitanLocation } from '../../../../../../models/location';
import { PaymentStatus, PaymentType } from '../../../../../../models/payment-enums';

@Component({
  selector: 'app-payment-filters-modal',
  templateUrl: './payment-filters-modal.component.html',
  styleUrls: ['./payment-filters-modal.component.css']
})
export class PaymentFiltersModalComponent implements OnInit {

  @Input() inputFilters: PaymentFilters;
  @Input() associationList: Association[];
  @Input() locationList: CapitanLocation[];
  @Input() savedCallback: (newFilters: PaymentFilters) => undefined;

  public filters: PaymentFilters;

  public discountAssociationSearchInput = '';

  public StatusOptions = [
    PaymentStatus.paymentComplete,
    PaymentStatus.paymentProcessing,
    PaymentStatus.paymentFailed,
    PaymentStatus.partiallyRefunded,
    PaymentStatus.refunded
  ];

  public PaymentMethodOptions = [
    PaymentType.stripe,
    PaymentType.gravity,
    PaymentType.mxMerchant,
    PaymentType.mock,
    PaymentType.lightspeed,
    PaymentType.square,
    PaymentType.credit,
    PaymentType.voucher,
    PaymentType.offline,
    PaymentType.posGiftCard,
    PaymentType.migrated
  ];

  public getPaymentTypeDisplay = getPaymentTypeDisplay;
  public getPaymentStatusDisplay = getPaymentStatusDisplay;

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService
  ) {}

  ngOnInit() {
    // Get a deep copy of the input filters. This ensures that the filters are
    // only updated once they are submitted, rather than as the user edits them.
    this.filters = {};

    for (const key of Object.keys(this.inputFilters)) {
      this.filters[key] = this.inputFilters[key];
    }
  }

  public handleSubmit() {
    // If all of the payment methods are selected, then remove the
    // payment_method_in field (as no filtering will be performed).
    if (this.allPaymentMethodsAreSelected()) {
      this.filters.payment_method_in = undefined;
    }
    if (this.filters.payment_method_in === '') {
      this.filters.payment_method_in = undefined;
    }

    // If all of the statuses are selected, then remove the status_in field (as
    // no filtering will be performed).
    if (this.allStatusesAreSelected()) {
      this.filters.status_in = undefined;
    }
    if (this.filters.status_in === '') {
      this.filters.status_in = undefined;
    }

    if (!this.filters.does_not_have_linked_customer) {
      this.filters.does_not_have_linked_customer = undefined;
    }

    this.savedCallback(this.filters);
    this.activeModal.close();
  }

  public handleClearAll() {
    // Clear all filters except for "search" (which is not edited in this
    // modal).
    this.filters = {search: this.filters.search};
  }

  public statusIsSelected(status: PaymentStatus) {
    if (!propExists(this.filters.status_in)) { return true; }

    const statusItems = this.filters.status_in.split(',');

    return statusItems.indexOf(status) > -1;
  }

  public allStatusesAreSelected() {
    let allStatusesSelected = true;
    for (const status of this.StatusOptions) {
      if (!this.statusIsSelected(status)) { allStatusesSelected = false; }
    }
    return allStatusesSelected;
  }

  public toggleStatus(status: PaymentStatus) {
    if (!propExists(this.filters.status_in)) { this.resetStatusIn(); }

    if (this.filters.status_in === '') {
      this.filters.status_in = status;
      return;
    }

    const statusItems = this.filters.status_in.split(',');

    const statusIdx = statusItems.indexOf(status);

    if (statusIdx > -1) {
      statusItems.splice(statusIdx, 1);
    } else {
      statusItems.push(status);
    }

    this.filters.status_in = statusItems.join(',');
  }

  public resetStatusIn() {
    this.filters.status_in = this.StatusOptions.join(',');
  }

  public paymentMethodIsSelected(paymentMethod: PaymentType) {
    if (!propExists(this.filters.payment_method_in)) { return true; }

    const paymentMethodOptions = this.filters.payment_method_in.split(',');

    return paymentMethodOptions.indexOf(paymentMethod) > -1;
  }

  public allPaymentMethodsAreSelected() {
    let allPaymentMethodsSelected = true;
    for (const paymentMethod of this.PaymentMethodOptions) {
      if (!this.paymentMethodIsSelected(paymentMethod)) {
        allPaymentMethodsSelected = false;
      }
    }
    return allPaymentMethodsSelected;
  }

  public togglePaymentMethod(paymentMethod: PaymentType) {
    if (!propExists(this.filters.payment_method_in)) { this.resetPaymentMethodIn(); }

    if (this.filters.payment_method_in === '') {
      this.filters.payment_method_in = paymentMethod;
      return;
    }

    const paymentMethodItems = this.filters.payment_method_in.split(',');

    const paymentMethodIdx = paymentMethodItems.indexOf(paymentMethod);

    if (paymentMethodIdx > -1) {
      paymentMethodItems.splice(paymentMethodIdx, 1);
    } else {
      paymentMethodItems.push(paymentMethod);
    }

    this.filters.payment_method_in = paymentMethodItems.join(',');
  }

  public resetPaymentMethodIn() {
    this.filters.payment_method_in = this.PaymentMethodOptions.join(',');
  }

  public handleAssociationTypeahead: OperatorFunction<string, readonly Association[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.searchAssociations(term).slice(0, 10))
    );

  public searchAssociations(term: string): Association[] {
    return searchList<Association>(
      term,
      this.associationList
        .sort((a: Association, b: Association) => {
          if (a.name > b.name) { return 1; }
          if (a.name < b.name) { return -1; }
          return 0;
        })
        .map(association => {
          return {
            value: association,
            searchableText: association.name
          }
        })
    );
  }

  public associationTypeaheadFormatter(association: Association): string {
    return association.name;
  }

  public getSelectedAssociationName(): string {
    for (const association of this.associationList) {
      if (association.id === this.filters.discount_association_id) { return association.name; }
    }
    return '[Unknown Association]';
  }

}
